import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Canvas,
  Image as FabricImage,
  Textbox,
  Rect,
  Ellipse,
  Circle,
  Line,
  Triangle,
} from "fabric";
import { Container, Row, Col, Card } from "react-bootstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { replaceInlinePlaceholdersAndStyles } from "components/CommonComponents/CertificatePreview";

const certificateValues = {
  trainee_name: "Amit Narayan Parmar",
  instructor_name: "Raj Chauhan",
  course_name: "AWS",
  issue_date: "12 Nov 2024",
  certificate_no: "raycham123",
  completion_date: "12 Dec 2024",
  certificate_valid_till: "12 Dec 2027",
};

const ViewCertificate = () => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [downloadOpen, setDownloadOpen] = useState(false);

  const certificateTemplate =
    location.state?.template ||
    JSON.parse(localStorage.getItem("currentTemplate"));

  useEffect(() => {
    if (!certificateTemplate) return;

    const { width, height } = certificateTemplate.canvasSize;

    const canvas = new Canvas(canvasRef.current, {
      width,
      height,
      selection: false,
    });

    fabricCanvasRef.current = canvas;

    const render = async () => {
      let isBlank = false;
      if (certificateTemplate.backgroundType === "image" && certificateTemplate.backgroundImageSrc) {
        const bgUrl = certificateTemplate.backgroundImageSrc;
        isBlank = typeof bgUrl === 'string' && bgUrl.includes("blank.png");
        const bg = await FabricImage.fromURL(bgUrl);
        bg.set({
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: "center",
          originY: "center",
          selectable: false,
          evented: false,
        });
        bg.scaleX = canvas.width / bg.width;
        bg.scaleY = canvas.height / bg.height;
        bg.setCoords();
        canvas.backgroundImage = bg;
      } else {
        isBlank = true;
        const bgUrl = require("assets/img/certificates_templates/blank.png");
        const bg = await FabricImage.fromURL(bgUrl);
        bg.set({
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: "center",
          originY: "center",
          selectable: false,
          evented: false,
        });
        bg.scaleX = canvas.width / bg.width;
        bg.scaleY = canvas.height / bg.height;
        bg.setCoords();
        canvas.backgroundImage = bg;
      }

      const upperCanvasEls = document.querySelectorAll(".upper-canvas");
      upperCanvasEls.forEach(el => {
        if (isBlank) el.classList.add("blank-canvas-border");
        else el.classList.remove("blank-canvas-border");
      });

      for (let item of certificateTemplate.layout) {
        if (item.type === "shape" && item.text) {
          try {
            const shapeData = JSON.parse(item.text);
            item = { ...item, ...shapeData };
          } catch (e) { }
        }

        const left = (item.x / 100) * canvas.width;
        const top = (item.y / 100) * canvas.height;

        if (item.type === "text") {


          let extraStyles = {};
          try {
            let srcStr = item.Src ?? item.src;
            if (srcStr) {
              const jsonStart = srcStr.indexOf('{');
              if (jsonStart !== -1) {
                srcStr = srcStr.substring(jsonStart);
                extraStyles = JSON.parse(srcStr);
              }
            }
          } catch (e) { }

          const originalStyles = item.styles || extraStyles.styles || {};
          const { value, styles: remappedStyles } = replaceInlinePlaceholdersAndStyles(item.text, originalStyles, certificateValues);

          canvas.add(
            new Textbox(value, {
              left,
              top,
              width: item.width || undefined,
              fontSize: item.fontSize,
              fontFamily: item.fontFamily,
              fontWeight: extraStyles.fontWeight || item.fontWeight || "normal",
              fontStyle: extraStyles.fontStyle || item.fontStyle || "normal",
              underline: extraStyles.underline !== undefined ? extraStyles.underline : (item.underline || false),
              fill: item.color || item.Color,
              angle: item.angle || item.Angle || 0,
              textAlign: item.textAlign || item.TextAlign || "left",
              originX: item.originX || item.OriginX || "center",
              originY: item.originY || item.OriginY || "center",
              scaleX: item.scaleX || item.ScaleX || 1,
              scaleY: item.scaleY || item.ScaleY || 1,
              lineHeight: item.lineHeight || 1,

              // Restore character-level formatting (remapped to match placeholder length)
              ...(Object.keys(remappedStyles).length > 0 && { styles: remappedStyles }),

              selectable: false,
              evented: false,
            }),
          );
        }

        if (item.type === "image") {
          const img = await FabricImage.fromURL(item.src);
          img.set({
            left,
            top,
            originX: item.originX || "left",
            originY: item.originY || "top",
            selectable: false,
            evented: false,
          });
          img.scaleToWidth(item.width);
          img.scaleToHeight(item.height);
          canvas.add(img);
        }

        if (item.type === "signature") {
          const img = await FabricImage.fromURL(item.src);

          img.set({
            left,
            top,
            originX: item.originX || "left",
            originY: item.originY || "top",
            angle: item.angle || 0,
            scaleX: item.scaleX || 1,
            scaleY: item.scaleY || 1,
            selectable: false,
            evented: false,
          });

          img.scaleToWidth(item.width);
          img.scaleToHeight(item.height);

          canvas.add(img);
        }

        if (item.type === "shape") {
          let shape;

          switch (item.shapeType) {
            case "ellipse":
              shape = new Ellipse({
                rx: item.rx,
                ry: item.ry,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
              });
              break;

            case "circle":
              shape = new Circle({
                radius: item.radius,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
              });
              break;

            case "rect":
              shape = new Rect({
                width: item.width,
                height: item.height,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
                scaleX: item.scaleX || 1,
                scaleY: item.scaleY || 1,
              });

              break;

            case "triangle":
              shape = new Triangle({
                width: item.width,
                height: item.height,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
              });
              break;

            case "line": {
              const x1 = item.X1 ?? item.x1 ?? 0;
              const y1 = item.Y1 ?? item.y1 ?? 0;
              const x2 = item.X2 ?? item.x2 ?? 100;
              const y2 = item.Y2 ?? item.y2 ?? 100;
              shape = new Line([x1, y1, x2, y2], {
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
              });
              break;
            }
          }

          if (shape) {
            shape.set({
              originX: (item.OriginX ?? item.originX) || "left",
              originY: (item.OriginY ?? item.originY) || "top",
              left,
              top,
              angle: (item.Angle ?? item.angle) || 0,
              scaleX: (item.ScaleX ?? item.scaleX) || 1,
              scaleY: (item.ScaleY ?? item.scaleY) || 1,
              selectable: false,
              evented: false,
            });

            shape.setCoords();
            canvas.add(shape);
          }
        }
      }

      canvas.renderAll();
    };

    render();

    return () => {
      canvas.dispose();

      const goingBackToDesigner = sessionStorage.getItem("fromDesignerToView");

      if (!goingBackToDesigner) {
        localStorage.removeItem("currentTemplate");
      }
    };
  }, [certificateTemplate]);

  // const toggleDownload = () => setDownloadOpen((v) => !v);

  // const downloadAsPNG = () => {
  //   const canvas = fabricCanvasRef.current;
  //   if (!canvas) return;

  //   const dataURL = canvas.toDataURL({
  //     format: "png",
  //     quality: 1,
  //     multiplier: 2, // higher resolution
  //   });

  //   const link = document.createElement("a");
  //   link.href = dataURL;
  //   link.download = `${certificateTemplate.name || "certificate"}.png`;

  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  // const downloadAsJPG = () => {
  //   const canvas = fabricCanvasRef.current;
  //   if (!canvas) return;

  //   const dataURL = canvas.toDataURL({
  //     format: "jpeg",
  //     quality: 0.95,
  //     multiplier: 2,
  //   });

  //   const link = document.createElement("a");
  //   link.href = dataURL;
  //   link.download = `${certificateTemplate.name || "certificate"}.jpg`;

  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  if (!certificateTemplate) {
    return (
      <div className="centered-message">
        <h3>No certificate template found</h3>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i
                      className="fa fa-chevron-left mr-3 cursor"
                      onClick={() => {
                        sessionStorage.setItem("fromDesignerToView", "true");
                        const role = localStorage.getItem("role") || "";
                        const templateId = location.state?.templateId;

                        let path = role === "SUPER_ADMIN"
                          ? "/super-admin/certificate/create-certificate"
                          : "/admin/certificate/create-certificate";

                        if (templateId) {
                          const encodedId = urlEncodedId(templateId);
                          path = role === "SUPER_ADMIN"
                            ? `/super-admin/certificate/edit-certificate/${encodedId}`
                            : `/admin/certificate/edit-certificate/${encodedId}`;
                        }

                        navigate(path, {
                          state: { fromView: true },
                        });
                      }}
                    />
                    <span className="font-weight-600">
                      Template Preview - {certificateTemplate.name}
                    </span>
                  </div>

                  {/* <div className="d-flex justify-content-end align-items-center">
                    <Dropdown isOpen={downloadOpen} toggle={toggleDownload}>
                      <DropdownToggle
                        caret
                        color="primary"
                        className="btn-deep-blue"
                      >
                        <i className="fa fa-download mr-1" />
                        Download
                      </DropdownToggle>

                      <DropdownMenu end>
                        <DropdownItem
                          onClick={() => {
                            setDownloadOpen(false);
                            downloadAsPNG();
                          }}
                        >
                          Download as PNG
                        </DropdownItem>

                        <DropdownItem
                          onClick={() => {
                            setDownloadOpen(false);
                            downloadAsJPG();
                          }}
                        >
                          Download as JPG
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div> */}
                </div>
              </Card.Header>

              <Card.Body className="d-flex justify-content-center align-items-center py-5">
                <canvas ref={canvasRef} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ViewCertificate;
